import type { Chart } from "chart.js";

interface ActiveTooltip {
  chart: Chart;
  element: HTMLElement;
}

let activeTooltip: ActiveTooltip | undefined;
let compatibilityTouchCanvas: HTMLCanvasElement | undefined;
let documentListenersRegistered = false;

const hideTooltip = ({ chart, element }: ActiveTooltip): void => {
  element.style.opacity = "0";
  if (!chart.canvas?.isConnected) return;
  chart.setActiveElements([]);
  chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
  chart.draw();
};

const hideActiveTooltip = (): void => {
  if (!activeTooltip) return;
  const tooltip = activeTooltip;
  activeTooltip = undefined;
  hideTooltip(tooltip);
};

const isEventInsideChart = (chart: Chart, event: Event): boolean =>
  event.target instanceof Node && chart.canvas.contains(event.target);

const clearCompatibilityTouch = (): void => {
  compatibilityTouchCanvas = undefined;
};

const clearStaleActiveTooltip = (): void => {
  if (
    activeTooltip &&
    (!activeTooltip.element.isConnected ||
      activeTooltip.element.style.opacity !== "1")
  ) {
    activeTooltip = undefined;
  }
};

const isTouchInteraction = (event: PointerEvent | TouchEvent): boolean =>
  event instanceof TouchEvent || event.pointerType !== "mouse";

const handleDocumentInteractionStart = (
  event: PointerEvent | TouchEvent
): void => {
  clearStaleActiveTooltip();
  clearCompatibilityTouch();
  if (activeTooltip && isEventInsideChart(activeTooltip.chart, event)) return;
  hideActiveTooltip();
  if (isTouchInteraction(event) && event.target instanceof HTMLCanvasElement) {
    compatibilityTouchCanvas = event.target;
  }
};

const handleDocumentClick = (): void => clearCompatibilityTouch();

const handleCompatibilityMouseEvent = (event: MouseEvent): void => {
  if (
    event.target instanceof Node &&
    compatibilityTouchCanvas?.contains(event.target)
  ) {
    event.stopImmediatePropagation();
  }
};

const registerDocumentListeners = (): void => {
  if (documentListenersRegistered) return;
  document.addEventListener(
    "pointerdown",
    handleDocumentInteractionStart,
    true
  );
  document.addEventListener("touchstart", handleDocumentInteractionStart, {
    capture: true,
    passive: true,
  });
  document.addEventListener("mousemove", handleCompatibilityMouseEvent, true);
  document.addEventListener("mouseout", handleCompatibilityMouseEvent, true);
  document.addEventListener("click", handleDocumentClick, true);
  documentListenersRegistered = true;
};

const unregisterDocumentListeners = (): void => {
  if (!documentListenersRegistered) return;
  document.removeEventListener(
    "pointerdown",
    handleDocumentInteractionStart,
    true
  );
  document.removeEventListener(
    "touchstart",
    handleDocumentInteractionStart,
    true
  );
  document.removeEventListener(
    "mousemove",
    handleCompatibilityMouseEvent,
    true
  );
  document.removeEventListener("mouseout", handleCompatibilityMouseEvent, true);
  document.removeEventListener("click", handleDocumentClick, true);
  documentListenersRegistered = false;
};

export const activateChartTooltip = (
  chart: Chart,
  element: HTMLElement
): void => {
  if (activeTooltip?.element !== element) hideActiveTooltip();
  activeTooltip = { chart, element };
};

export const deactivateChartTooltip = (element: HTMLElement): void => {
  element.style.opacity = "0";
  if (activeTooltip?.element === element) activeTooltip = undefined;
};

if (typeof document !== "undefined") registerDocumentListeners();

if (import.meta.hot) import.meta.hot.dispose(unregisterDocumentListeners);
